import React, { useState, useMemo } from 'react';
import { ShoppingBag, ChevronLeft, Search, Filter, CheckCircle, Clock, XCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';

const MerchandiseOrders = ({ expenses, players, clubId, onBack }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewMode, setViewMode] = useState('grouped'); // 'list' or 'grouped'
    const [expandedGroups, setExpandedGroups] = useState({});

    // Filter expenses to identify merchandise orders
    const orders = useMemo(() => {
        if (!expenses) return [];

        return expenses.filter(expense =>
            // Check for explicit type or category that identifies merchandise
            (expense.type && expense.type.toLowerCase() === 'merchandise') ||
            (expense.category && expense.category.toLowerCase() === 'merchandise') ||
            (expense.isMerchandise === true) ||
            // Fallback: Check if description contains "Order"
            (expense.description && expense.description.includes('Merchandise Order'))
        ).map(order => {
            // Resolve player name
            let buyerName = 'Unknown';
            let buyerId = null;

            // Updated logic: Check userId first (standard for purchases), then splitAmong
            if (order.userId) {
                buyerId = order.userId;
            } else if (order.splitAmong && order.splitAmong.length > 0) {
                buyerId = order.splitAmong[0];
            }

            if (buyerId) {
                const buyer = players.find(p => p.id === buyerId);
                buyerName = buyer ? buyer.name : 'Unknown Player';
            }

            // Map date field (PlayerDashboard saves as 'createdAt')
            const orderDate = order.date || order.createdAt || new Date().toISOString();

            return {
                ...order,
                buyerName,
                buyerId,
                date: orderDate,
                status: 'pending'
            };
        });
    }, [expenses, players]);

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const matchesSearch =
                (order.buyerName && order.buyerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (order.description && order.description.toLowerCase().includes(searchTerm.toLowerCase()));

            if (statusFilter === 'all') return matchesSearch;
            return matchesSearch && order.status === statusFilter;
        });
    }, [orders, searchTerm, statusFilter]);

    // Group orders by player
    const groupedOrders = useMemo(() => {
        const groups = {};
        filteredOrders.forEach(order => {
            const key = order.buyerId || order.buyerName;
            if (!groups[key]) {
                groups[key] = {
                    id: key,
                    name: order.buyerName,
                    orders: [],
                    totalAmount: 0
                };
            }
            groups[key].orders.push(order);
            groups[key].totalAmount += Number(order.amount) || 0;
        });

        // Sort groups by total spend descending
        return Object.values(groups).sort((a, b) => b.totalAmount - a.totalAmount);
    }, [filteredOrders]);

    const toggleGroup = (id) => {
        setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div className="max-w-6xl mx-auto">
            <button
                onClick={onBack}
                className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
            >
                <ChevronLeft size={20} />
                Back to Reports
            </button>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200 flex flex-col items-start gap-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between w-full gap-4">
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-2">
                                <ShoppingBag className="text-purple-600" size={28} />
                                Merchandise Orders
                            </h1>
                            <p className="text-gray-500 mt-1">Track and manage player merchandise purchases</p>
                        </div>

                        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setViewMode('grouped')}
                                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'grouped'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                By Player
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'list'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                All Orders
                            </button>
                        </div>
                    </div>

                    <div className="w-full relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder={viewMode === 'grouped' ? "Search players..." : "Search orders..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none w-full md:w-80"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto min-h-[400px]">
                    {filteredOrders.length === 0 ? (
                        <div className="p-12 text-center text-gray-400">
                            <ShoppingBag size={48} className="mx-auto mb-3 opacity-20" />
                            <p>No merchandise orders found</p>
                        </div>
                    ) : viewMode === 'grouped' ? (
                        <div className="divide-y divide-gray-100">
                            <div className="grid grid-cols-12 bg-gray-50 p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                <div className="col-span-6 md:col-span-5">Player Name</div>
                                <div className="col-span-3 text-center">Orders</div>
                                <div className="col-span-3 text-right">Total Spent</div>
                                <div className="col-span-1"></div>
                            </div>
                            {groupedOrders.map(group => (
                                <div key={group.id} className="bg-white">
                                    <div
                                        className="grid grid-cols-12 p-4 items-center hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => toggleGroup(group.id)}
                                    >
                                        <div className="col-span-6 md:col-span-5 flex items-center gap-3">
                                            {expandedGroups[group.id] ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                                            <span className="font-bold text-gray-900">{group.name}</span>
                                        </div>
                                        <div className="col-span-3 text-center text-gray-600 bg-gray-50 rounded-full py-1 text-sm inline-block mx-auto px-3 w-fit">
                                            {group.orders.length} items
                                        </div>
                                        <div className="col-span-3 text-right font-bold text-purple-700">
                                            {formatCurrency(group.totalAmount)}
                                        </div>
                                    </div>

                                    {expandedGroups[group.id] && (
                                        <div className="bg-gray-50 px-4 pb-4 md:px-12 md:pb-6">
                                            <table className="w-full text-left border-collapse bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                <thead>
                                                    <tr className="bg-gray-100 text-gray-500 text-xs border-b border-gray-200">
                                                        <th className="p-3 font-medium">Date</th>
                                                        <th className="p-3 font-medium">Item Description</th>
                                                        <th className="p-3 font-medium text-right">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.orders.map((order, idx) => (
                                                        <tr key={order.id || idx} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                                                            <td className="p-3 text-sm text-gray-600">{formatDate(order.date)}</td>
                                                            <td className="p-3 text-sm text-gray-800">
                                                                {order.description.replace(/\s*\(\s*\)/g, '').trim()}
                                                            </td>
                                                            <td className="p-3 text-sm font-medium text-right text-gray-900">
                                                                {formatCurrency(order.amount)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wide">
                                    <th className="p-4 font-semibold">Date</th>
                                    <th className="p-4 font-semibold">Player</th>
                                    <th className="p-4 font-semibold">Items / Description</th>
                                    <th className="p-4 font-semibold text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredOrders.map((order) => (
                                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4 text-sm text-gray-600">
                                            {formatDate(order.date)}
                                        </td>
                                        <td className="p-4 font-medium text-gray-900">
                                            {order.buyerName}
                                        </td>
                                        <td className="p-4 text-gray-600 max-w-sm">
                                            {order.description.replace(/\s*\(\s*\)/g, '').trim()}
                                        </td>
                                        <td className="p-4 text-right font-bold text-gray-900">
                                            {formatCurrency(order.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 bg-gray-50 text-xs text-center text-gray-500">
                    Showing {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''} across {Object.keys(groupedOrders).length} players
                </div>
            </div>
        </div>
    );
};

export default MerchandiseOrders;
